import { useState } from 'react';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { VisitsResponse } from '../@types/visits';
import { useGetVideaId } from '../utils/hooks/useGetVideaId';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';
import { usePreferences } from './usePreferences';

interface UsePatientVisitsParams {
  limit?: number;
  options?: UseQueryOptions<VisitsResponse>;
  patientId: string;
  videaId?: string; // Optional videaId to override lookup
}

export const usePatientVisits = ({
  limit = 50,
  options,
  patientId,
  videaId: providedVideaId,
}: UsePatientVisitsParams) => {
  const { data: prefs } = usePreferences();
  const { primaryPracticeId: currentPracticeId } = useSelector(
    (state: any) => state.login.user,
  );
  const lookedUpVideaId = useGetVideaId(patientId);
  // Use provided videaId if available, otherwise fall back to looked up value
  const videaId = providedVideaId || lookedUpVideaId;
  const [shouldRetryWithEndpointV1, setShouldRetryWithEndpointV1] =
    useState(false);

  const path = `${endpoints.patient}/${patientId}/${endpoints.visits}`;
  const pathV2 = `${endpoints.v2}/${endpoints.patient}/${videaId}/${endpoints.visits}`;

  const isEndpointV1Enabled =
    !!currentPracticeId &&
    !!patientId &&
    !!prefs &&
    (!prefs.enablePatientsV2 || !videaId);

  const result = useQuery({
    queryKey: [path],
    queryFn: () =>
      makeAPIRequestReactQuery<VisitsResponse>(path, {
        limit,
        filterEmpty: true,
      }),
    enabled: isEndpointV1Enabled || shouldRetryWithEndpointV1,
    ...options,
  });

  const resultV2 = useQuery({
    queryKey: [pathV2],
    queryFn: () =>
      makeAPIRequestReactQuery<VisitsResponse>(pathV2, {
        limit,
      }),
    enabled: !!prefs?.enablePatientsV2 && !!currentPracticeId && !!videaId,
    onError: () => {
      // If no patient is found via the V2 endpoint, then retry visits with the
      // V1 endpoint.
      setShouldRetryWithEndpointV1(!!currentPracticeId && !!patientId);
    },
    ...options,
  });

  return isEndpointV1Enabled || shouldRetryWithEndpointV1 ? result : resultV2;
};
